import type { GoalType } from '@/types/api';

/**
 * Wizard stepper bounds + defaults — see READING_TRACKER_PLAN.md §8.
 *
 * `max` is omitted for chapters/pages because the real upper bound is the
 * book's totalUnits — the stepper component caps against `book.chapters` /
 * `book.pages` at render time.
 */
export const STEPPER_BOUNDS: Record<
  GoalType,
  { default: number; min: number; step: number; max?: number }
> = {
  chapters: { default: 2, min: 1, step: 1 },
  pages: { default: 5, min: 1, step: 1 },
  // Minutes is no longer offered in the picker (see GOAL_TYPE_OPTIONS
  // below) but the entry stays so legacy `minutes`-mode goals already in
  // the database keep rendering. Step kept at 5 for those goals.
  minutes: { default: 20, min: 1, step: 5, max: 180 },
};

/**
 * Used when a book has no `pages` value and the reader picks "minutes".
 * 1 page ≈ 1 minute per the spec; falling back to 180 gives the wizard
 * room without making the dot grid unreasonable.
 */
export const FALLBACK_MINUTES = 180;

/**
 * Random encouragement messages surfaced via Sonner on each successful
 * day-mark. The spec lists 10; keep them upbeat and short.
 */
export const ENCOURAGEMENTS = [
  '🎉 Way to go! Keep it up!',
  '⭐ Fantastic reading!',
  "🔥 You're on fire!",
  '🦁 Roar! Reading champion!',
  '🚀 Blast off! Amazing!',
  '🌈 Spectacular! So proud of you!',
  "💫 You're a reading superstar!",
  '🏆 Champion move! Keep going!',
  '🎈 Woohoo! Another day done!',
  "📚 Books beware — you're unstoppable!",
] as const;

/**
 * Plan-summary highlight phrases (Screen 4). One is randomly selected
 * each time the summary screen renders to keep repeat viewings fresh.
 */
export const PLAN_PHRASES: Array<
  (p: { perDay: number; goalType: GoalType; totalDays: number }) => string
> = [
  ({ perDay, goalType, totalDays }) =>
    `You'll read ${perDay} ${goalType} a day — that's it! Easy peasy. You'll be done in just ${totalDays} days. 🌟`,
  ({ perDay, goalType, totalDays }) =>
    `Only ${perDay} ${goalType} each day and you'll crush this book in ${totalDays} days. You've totally got this! 💪`,
  ({ perDay, goalType, totalDays }) =>
    `${totalDays} days from now you'll have finished the whole book! Just ${perDay} ${goalType} a day gets you there. 🚀`,
];

/**
 * Display copy for the goal-type cards on the goal-setup screen. Icons
 * are emojis (rendered inline) — no extra asset fetch required.
 */
export interface GoalTypeOption {
  type: GoalType;
  icon: string;
  label: string;
}

// Note: 'minutes' is no longer offered in the wizard. The GoalType union
// and STEPPER_BOUNDS still include it so legacy `minutes`-mode goals
// already in the database continue to render in ProgressScreen / on the
// server without a migration; the picker just doesn't surface it.
export const GOAL_TYPE_OPTIONS: ReadonlyArray<GoalTypeOption> = [
  { type: 'chapters', icon: '📑', label: 'Chapters' },
  { type: 'pages', icon: '📄', label: 'Pages' },
];
